import { GraphPair } from '@models/meter-scans/ofdma/ofdma-graph-pair';
import { OdfmaMetaData } from '@models/meter-scans/ofdma/ofdma-meta-data';
import { OfdmaRaw } from '@models/meter-scans/ofdma/ofdma-raw';
import { UnitConversionUtils } from '@utilities/unit-conversion-utils';
import { ClassCast } from '@utilities/object-helper';

export class OfdmaScan {
    locked: boolean;
    @ClassCast(GraphPair)
    groupDelay: GraphPair[];
    @ClassCast(OfdmaRaw)
    raw: OfdmaRaw;
    @ClassCast(GraphPair)
    impulse: GraphPair[];
    @ClassCast(GraphPair)
    icfr: GraphPair[];
    @ClassCast(GraphPair)
    transmit: GraphPair[];
    @ClassCast(GraphPair)
    unwrappedPhase: GraphPair[];
    @ClassCast(GraphPair)
    wrappedPhase: GraphPair[];
    @ClassCast(GraphPair)
    icfrTilt: GraphPair[];
    initialMetaData: OdfmaMetaData;

    generateInitialMetaData(): void {
        if (!this.initialMetaData) {
            const subcarrierZeroFrequency = UnitConversionUtils.hzToMhz(this.raw.subcarrierZeroFrequency);
            const subcarrierSpacing = UnitConversionUtils.khzToMhz(this.raw.subcarrierSpacing);
            const freqStart = parseFloat((subcarrierZeroFrequency + this.raw.firstActiveSubcarrierNumber * subcarrierSpacing).toFixed(1));
            const freqEnd = parseFloat((subcarrierZeroFrequency + this.raw.lastActiveSubcarrierNumber * subcarrierSpacing).toFixed(1));

            this.initialMetaData = {
                subcarrierSpacing: this.raw.subcarrierSpacing + ' kHz',
                usChannelId: this.raw.preEq.usChannelId,
                transmitPower: this.raw.transmitPower ,
                transmitPowerUnits:  'dBmV' ,
                freqStart: freqStart,
                freqStartUnits: 'MHz',
                freqEnd: freqEnd,
                freqEndUnits: 'MHz',
                freqWidth: parseFloat((freqEnd - freqStart).toFixed(1)),
                freqWidthUnits: 'MHz',
                centerFrequency: parseFloat((freqStart + ((freqEnd - freqStart)/2)).toFixed(1)),
              // TODO: need to get the actual value from the backend
              rx: 0,
              mer: 0,
              portMer: 0
            };
        }
    }

    get groupDelayWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.groupDelay,
            metaData: Object.assign({
                // unique
                ripplePeakToPeak: parseFloat(this.raw.preEq.GDRPP.toFixed(1)) + ' nSec',
                rippleRms: parseFloat(this.raw.preEq.GDRs.toFixed(1)) + ' nSec',
                slope: parseFloat(this.raw.preEq.GDS.toFixed(1)) + ' nSec per MHz',
                mean: parseFloat(this.raw.preEq.GDMean.toFixed(1)) + ' nSec',
            }, this.initialMetaData)
        };
    }

    get impulseWithMetaData() {
        this.generateInitialMetaData();
        const endPosition: any = this.impulse.length - 1;
        const dbEndValue = parseFloat(this.impulse[endPosition].y.toFixed(1));
        const dbStartValue = parseFloat(this.impulse[0].y.toFixed(1));

        return {
            data: this.impulse,
            metaData: Object.assign({
                // unique
                dbAtStart: dbStartValue + 'ft',
                dbAtEnd: dbEndValue + 'ft'
            }, this.initialMetaData)
        };
    }

    get icfrWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.icfr,
            icfrTiltData: this.icfrTilt,
            metaData: Object.assign({
                // unique
                ripplePeakToPeak: parseFloat(this.raw.preEq.ARPP.toFixed(1)) + ' dB',
                rippleRms: parseFloat(this.raw.preEq.ARRs.toFixed(1)) + ' dB',
                slope: parseFloat(this.raw.preEq.AS.toFixed(1)) + ' dB per MHz',
                mean: parseFloat(this.raw.preEq.AMean.toFixed(1)) + ' dB'
            }, this.initialMetaData)
        };
    }

    get transmitWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.transmit,
            metaData: Object.assign({
                // unique
            }, this.initialMetaData)
        };
    }

    get transmitWithIucData() {
        const iucProfileData: Record<string, string | number>[] = [];
        if (this.raw.iucProfile) {
            this.raw.iucProfile.forEach(profile => {
                iucProfileData.push({
                    iuc: profile.iuc,
                    dataSymbolModulation: profile.dataSymbolModulation,
                    discontinuityTime: profile.discontinuityTime + ' sec',
                    outFramesCounter: profile.outFramesCounter
                });
            });
        }

        return {
            data: iucProfileData
        };
    }

    get wrappedPhaseWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.wrappedPhase,
            metaData: this.initialMetaData
        };
    }

    get unwrappedPhaseWithMetaData() {
        this.generateInitialMetaData();

        return {
            data: this.unwrappedPhase,
            metaData: this.initialMetaData
        };
    }
}
